import { useMemo, useState } from "react";
import {
  fetchIdentityConflicts,
  postResolveConflict,
  type IdentityConflict,
  type MatchedUserSummary,
} from "../lib/api";
import { useApi } from "../hooks/use-api";
import { useDocumentTitle } from "../hooks/use-document-title";

const DRIVEWHEEL_USER_URL = "https://drivewheel.tradebidauctions.com/desk/legacy-user/LU-";

type ConflictView = "open" | "resolved";

function sortConflicts(
  conflicts: IdentityConflict[],
  view: ConflictView,
): IdentityConflict[] {
  if (view === "open") {
    return [...conflicts].sort((a, b) => b.createdAt.localeCompare(a.createdAt));
  }
  return [...conflicts].sort((a, b) =>
    (b.resolvedAt ?? b.createdAt).localeCompare(a.resolvedAt ?? a.createdAt),
  );
}

function statusBadge(status: string): string {
  return status === "open" ? "badge badge-warning" : "badge badge-success";
}

function formatLastActivity(iso: string | null): string {
  if (!iso) return "No bids on record";
  return new Date(iso).toLocaleString();
}

function fullName(user: MatchedUserSummary): string {
  const composed = `${user.firstName ?? ""} ${user.lastName ?? ""}`.trim();
  return composed || "(no name on record)";
}

function MatchedUserCard({
  user,
  isChosen,
  isResolved,
  onChoose,
  submitting,
}: {
  user: MatchedUserSummary;
  isChosen: boolean;
  isResolved: boolean;
  onChoose: () => void;
  submitting: boolean;
}) {
  const cardStyle: React.CSSProperties = {
    border: isChosen
      ? "2px solid var(--color-accent)"
      : "1px solid var(--color-border)",
    borderRadius: "var(--radius)",
    padding: 14,
    background: isChosen
      ? "rgba(53, 211, 170, 0.08)"
      : "var(--color-surface-solid)",
    display: "flex",
    flexDirection: "column",
    gap: 6,
    minWidth: 280,
    flex: "1 1 280px",
    maxWidth: 380,
  };
  return (
    <div style={cardStyle}>
      <div style={{ fontWeight: 600, color: "var(--color-text)" }}>
        {fullName(user)}{" "}
        <span style={{ color: "var(--color-text-soft)", fontWeight: 400, fontSize: 12 }}>
          #{user.userId}
        </span>
      </div>
      {user.dealership && (
        <div style={{ fontSize: 13, color: "var(--color-text)" }}>{user.dealership}</div>
      )}
      {user.orgName && user.orgName !== user.dealership && (
        <div style={{ fontSize: 12, color: "var(--color-text-secondary)" }}>
          {user.orgName}
        </div>
      )}
      <div style={{ fontSize: 12, color: "var(--color-text-secondary)" }}>
        Last bid: {formatLastActivity(user.lastBidAt)}
      </div>
      <div
        style={{
          display: "flex",
          gap: 12,
          marginTop: 8,
          alignItems: "center",
          flexWrap: "wrap",
        }}
      >
        <a
          href={`${DRIVEWHEEL_USER_URL}${user.userId}`}
          target="_blank"
          rel="noopener noreferrer"
          className="dealer-link"
          style={{ fontSize: 12 }}
        >
          View on DriveWheel ↗
        </a>
        {!isResolved && (
          <button
            className="btn btn-sm btn-primary"
            type="button"
            disabled={submitting}
            onClick={onChoose}
          >
            {submitting && isChosen ? "Saving..." : "Choose this user"}
          </button>
        )}
        {isResolved && isChosen && (
          <span className="badge badge-success" style={{ fontSize: 11 }}>
            Resolved to this user
          </span>
        )}
      </div>
    </div>
  );
}

function ResolveSection({
  conflict,
  onResolved,
}: {
  conflict: IdentityConflict;
  onResolved: () => void;
}) {
  const [resolution, setResolution] = useState("");
  const [submittingId, setSubmittingId] = useState<number | null>(null);

  const isResolved = conflict.status === "resolved";

  async function choose(userId: number) {
    setSubmittingId(userId);
    try {
      await postResolveConflict({
        phoneNumber: conflict.phoneNumber,
        resolution: resolution.trim(),
        resolvedUserId: userId,
      });
      onResolved();
    } finally {
      setSubmittingId(null);
    }
  }

  if (conflict.matchedUsers.length === 0) {
    return (
      <div style={{ fontSize: 12, color: "var(--color-text-secondary)" }}>
        No user details available for matched IDs: {conflict.matchedUserIds.join(", ")}
      </div>
    );
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 12 }}>
        {conflict.matchedUsers.map((user) => (
          <MatchedUserCard
            key={user.userId}
            user={user}
            isChosen={conflict.resolvedUserId === user.userId}
            isResolved={isResolved}
            submitting={submittingId === user.userId}
            onChoose={() => choose(user.userId)}
          />
        ))}
      </div>
      {!isResolved && (
        <input
          type="text"
          value={resolution}
          onChange={(e) => setResolution(e.target.value)}
          placeholder="Optional resolution note (e.g. 'duplicate account, picked active one')"
          style={{
            width: "100%",
            maxWidth: 600,
            padding: "8px 12px",
            fontSize: 13,
            borderRadius: "var(--radius-sm)",
            border: "1px solid var(--color-border)",
            background: "var(--color-surface)",
            color: "var(--color-text)",
          }}
        />
      )}
      {isResolved && conflict.resolution && (
        <div style={{ fontSize: 12, color: "var(--color-text-secondary)" }}>
          Note: {conflict.resolution}
          {conflict.resolvedBy && ` (by ${conflict.resolvedBy})`}
        </div>
      )}
    </div>
  );
}

function ViewTab({
  active,
  count,
  onClick,
  children,
}: {
  active: boolean;
  count: number;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        padding: "8px 14px",
        borderRadius: "var(--radius-sm)",
        border: "1px solid var(--color-border)",
        background: active ? "var(--color-accent)" : "var(--color-surface)",
        color: active ? "#072018" : "var(--color-text)",
        fontWeight: 500,
        fontSize: 13,
        cursor: "pointer",
        display: "flex",
        alignItems: "center",
        gap: 8,
      }}
    >
      <span>{children}</span>
      <span
        style={{
          fontSize: 11,
          padding: "1px 7px",
          borderRadius: 10,
          background: active ? "rgba(7, 32, 24, 0.18)" : "var(--color-border)",
          color: active ? "#072018" : "var(--color-text-secondary)",
          fontWeight: 600,
        }}
      >
        {count}
      </span>
    </button>
  );
}

export function IdentityConflictsPage() {
  useDocumentTitle("Identity Conflicts");
  const { data, loading, error, refetch } = useApi(fetchIdentityConflicts);
  const [view, setView] = useState<ConflictView>("open");

  const { openCount, resolvedCount, visible } = useMemo(() => {
    const all = data ?? [];
    const open = all.filter((c) => c.status === "open");
    const resolved = all.filter((c) => c.status === "resolved");
    return {
      openCount: open.length,
      resolvedCount: resolved.length,
      visible: sortConflicts(view === "open" ? open : resolved, view),
    };
  }, [data, view]);

  return (
    <>
      <div className="card-header">
        <h1 className="page-title">Identity Conflicts</h1>
        <button className="btn btn-sm" type="button" onClick={refetch}>
          Refresh
        </button>
      </div>

      {!loading && !error && data && (
        <div style={{ display: "flex", gap: 8, marginBottom: 16 }}>
          <ViewTab active={view === "open"} count={openCount} onClick={() => setView("open")}>
            Open
          </ViewTab>
          <ViewTab
            active={view === "resolved"}
            count={resolvedCount}
            onClick={() => setView("resolved")}
          >
            Resolved
          </ViewTab>
        </div>
      )}

      {loading && <div className="loading">Loading conflicts...</div>}
      {error && <div className="error-msg">{error}</div>}

      {data && visible.length === 0 && (
        <div className="empty-state">
          {view === "open" ? "No open conflicts" : "No resolved conflicts yet"}
        </div>
      )}

      {visible.length > 0 && (
        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          {visible.map((conflict) => (
            <div
              key={conflict.conflictId}
              style={{
                border: "1px solid var(--color-border)",
                borderRadius: "var(--radius)",
                padding: 16,
                background: "var(--color-surface)",
                boxShadow: "var(--shadow)",
              }}
            >
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  marginBottom: 14,
                  gap: 12,
                  flexWrap: "wrap",
                }}
              >
                <div>
                  <div
                    className="mono"
                    style={{ fontWeight: 600, color: "var(--color-text)", fontSize: 15 }}
                  >
                    {conflict.phoneNumber}
                  </div>
                  <div style={{ fontSize: 12, color: "var(--color-text-secondary)", marginTop: 2 }}>
                    Detected {new Date(conflict.createdAt).toLocaleString()}
                    {conflict.resolvedAt &&
                      ` · Resolved ${new Date(conflict.resolvedAt).toLocaleString()}`}
                  </div>
                </div>
                <span className={statusBadge(conflict.status)}>{conflict.status}</span>
              </div>
              <ResolveSection conflict={conflict} onResolved={refetch} />
            </div>
          ))}
        </div>
      )}
    </>
  );
}
